/**
 * Vocabulário e iconografia do menu por segmento de negócio.
 *
 * O módulo `bookings` é agnóstico: o mesmo `Order` é uma cirurgia numa clínica,
 * uma escova progressiva num salão, uma sessão de treino numa academia e uma revisão
 * numa oficina. O modelo é o mesmo; o que muda é como cada segmento chama a coisa —
 * e com que ícone.
 *
 * Este arquivo concentra essa tradução. Antes ela vivia espalhada em condicionais
 * dentro do `admin-sidebar.tsx` (o caso `Pacientes`/`Clientes`), o que fazia cada
 * termo novo virar mais um `? :` aninhado no meio do JSX.
 *
 * **O padrão é sempre o da saúde.** O label vem do backend
 * (`SystemModule.label` / `resources[].label`) e o ícone de `sidebar-config.ts`,
 * ambos escritos na linguagem clínica — a base instalada tem tutoriais e prints
 * com eles. Só os OUTROS segmentos são traduzidos, aqui.
 *
 * O que NÃO mora aqui: quem pode ver o quê. Isso é decidido pelo plano
 * (`PlanTier`) e pelas permissões da role — segmento só governa a apresentação e
 * o que é ruído para aquele ramo.
 */

import {
    MdBadge,
    MdContentPasteSearch,
    MdDesignServices,
    MdEventAvailable,
    MdFactCheck,
    MdStars,
} from 'react-icons/md'
import type { IconType } from 'react-icons'

/** Valores de `CompanySegment` (schema.prisma). */
export type Segment =
    | 'HEALTH'
    | 'BEAUTY'
    | 'FITNESS'
    | 'PET_SHOP'
    | 'DIRECT_SALE'
    | 'QUOTE_BASED_SALE'
    | 'OTHER'

/** Todo segmento que não é saúde. `HEALTH` nunca é traduzido. */
const NON_HEALTH: Segment[] = [
    'BEAUTY',
    'FITNESS',
    'PET_SHOP',
    'DIRECT_SALE',
    'QUOTE_BASED_SALE',
    'OTHER',
]

/**
 * Monta um mapa por segmento a partir de uma base comum a todos os não-saúde,
 * com ajustes pontuais por ramo. Evita repetir a mesma tabela seis vezes — e
 * evita que uma entrada nova entre em cinco segmentos e esqueça o sexto.
 */
function byNonHealthSegment<T>(
    base: Record<string, T>,
    overrides: Partial<Record<Segment, Record<string, T>>> = {}
): Partial<Record<Segment, Record<string, T>>> {
    const result: Partial<Record<Segment, Record<string, T>>> = {}
    for (const segment of NON_HEALTH) {
        result[segment] = { ...base, ...(overrides[segment] ?? {}) }
    }
    return result
}

/**
 * Rótulo de um recurso fora da saúde. Chave: `<módulo>:<recurso>`.
 * Ausente = usa o label do backend.
 */
const RESOURCE_LABEL_BY_SEGMENT = byNonHealthSegment<string>(
    {
        'person:clients': 'Clientes',
        'bookings:consultations': 'Avaliações',
        'bookings:orders': 'Pedidos',
        // O cadastro é de serviços prestados (usado no fiscal). "Médicos" no nome
        // não cabe fora da saúde, e "Serviços" sozinho colidiria com o recurso de
        // agendamento, que já se chama assim.
        'registrations:medical_services': 'Serviços Prestados',
    },
    {
        FITNESS: {
            'person:clients': 'Alunos',
            'bookings:exams': 'Avaliações Físicas',
        },
        PET_SHOP: { 'person:clients': 'Tutores' },
        BEAUTY: { 'bookings:packages': 'Pacotes' },
    }
)

/**
 * Ícone de um recurso fora da saúde.
 *
 * Os padrões são explicitamente clínicos — estetoscópio em "Profissionais",
 * microscópio em "Exames", leito hospitalar em "Procedimento" —, e num salão de
 * beleza ou numa oficina eles comunicam a coisa errada mesmo depois de o rótulo
 * ter sido traduzido.
 */
const RESOURCE_ICON_BY_SEGMENT = byNonHealthSegment<IconType>({
    // FaUserDoctor (médico de jaleco) -> crachá
    'person:professionals': MdBadge,
    // FaNotesMedical (prontuário) -> avaliação concluída
    'bookings:consultations': MdFactCheck,
    // MdBiotech (microscópio) -> inspeção/análise
    'bookings:exams': MdContentPasteSearch,
    // FaProcedures (leito hospitalar) -> prestação de serviço
    'bookings:orders': MdDesignServices,
    // MdMedicalServices (maleta médica) -> especialidade genérica
    'registrations:specialties': MdStars,
})

/** Rótulo do módulo fora da saúde. */
const MODULE_LABEL_BY_SEGMENT = byNonHealthSegment<string>({
    bookings: 'Agendamentos e Serviços',
})

/** Ícone do módulo fora da saúde (o padrão é `MdLocalHospital`). */
const MODULE_ICON_BY_SEGMENT = byNonHealthSegment<IconType>({
    bookings: MdEventAvailable,
})

/**
 * Recursos que são ruído fora da saúde. Não é uma trava de segurança — o backend
 * continua validando a permissão; é só para o menu não oferecer "Receituário" a
 * um salão de beleza.
 */
const HIDE_RESOURCE_BY_SEGMENT: Partial<Record<Segment, string[]>> =
    Object.fromEntries(
        NON_HEALTH.map(segment => [
            segment,
            [
                'triage',
                'prescriptions',
                'certificates',
                'digital_certificates',
                'medical-records',
                'health_plans',
            ],
        ])
    )

/**
 * Rótulo de um recurso para o segmento, ou `fallback` (o label do backend) quando
 * o segmento não redefine o termo.
 */
export function resourceLabel(
    segment: string,
    moduleKey: string,
    resourceKey: string,
    fallback: string
): string {
    return (
        RESOURCE_LABEL_BY_SEGMENT[segment as Segment]?.[
            `${moduleKey}:${resourceKey}`
        ] ?? fallback
    )
}

/** Ícone de um recurso para o segmento, ou `fallback` (o de `sidebar-config.ts`). */
export function resourceIcon(
    segment: string,
    moduleKey: string,
    resourceKey: string,
    fallback: IconType
): IconType {
    return (
        RESOURCE_ICON_BY_SEGMENT[segment as Segment]?.[
            `${moduleKey}:${resourceKey}`
        ] ?? fallback
    )
}

/** Rótulo do módulo para o segmento, ou `fallback` (o label do backend). */
export function moduleLabel(
    segment: string,
    moduleKey: string,
    fallback: string
): string {
    return MODULE_LABEL_BY_SEGMENT[segment as Segment]?.[moduleKey] ?? fallback
}

/** Ícone do módulo para o segmento, ou `fallback` (o de `sidebar-config.ts`). */
export function moduleIcon(
    segment: string,
    moduleKey: string,
    fallback: IconType
): IconType {
    return MODULE_ICON_BY_SEGMENT[segment as Segment]?.[moduleKey] ?? fallback
}

/** `true` quando o recurso é ruído para o segmento e deve sair do menu. */
export function isResourceHiddenForSegment(
    segment: string,
    resourceKey: string
): boolean {
    return !!HIDE_RESOURCE_BY_SEGMENT[segment as Segment]?.includes(resourceKey)
}
